import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'

interface Stats {
  totalSessions: number
  activeSessions: number
  totalTrainees: number
  completedSessions: number
}

interface Session {
  id: string
  title: string
  type: string
  status: string
  startDate: string
  _count: { trainees: number }
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  PLANNED:     { label: 'Planifiée',   color: 'bg-accent/10 border-accent/25 text-accent' },
  IN_PROGRESS: { label: 'En cours',    color: 'bg-warning/10 border-warning/25 text-warning' },
  COMPLETED:   { label: 'Terminée',    color: 'bg-success/10 border-success/25 text-success' },
  CANCELLED:   { label: 'Annulée',     color: 'bg-white/[0.06] border-white/[0.12] text-text-muted' },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function CfDashboardPage() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiFetch<Session[]>('/training/sessions')
      .then(setSessions)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const stats: Stats = {
    totalSessions: sessions.length,
    activeSessions: sessions.filter((s) => s.status === 'IN_PROGRESS' || s.status === 'PLANNED').length,
    totalTrainees: sessions.reduce((sum, s) => sum + s._count.trainees, 0),
    completedSessions: sessions.filter((s) => s.status === 'COMPLETED').length,
  }

  const recent = sessions.slice(0, 5)

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Vue d'ensemble</h1>
        <p className="text-text-secondary text-sm mt-1">Bienvenue dans votre espace centre de formation</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Sessions totales', value: stats.totalSessions, color: 'text-accent' },
          { label: 'Sessions actives', value: stats.activeSessions, color: 'text-warning' },
          { label: 'Stagiaires formés', value: stats.totalTrainees, color: 'text-success' },
          { label: 'Sessions terminées', value: stats.completedSessions, color: 'text-text-secondary' },
        ].map((kpi) => (
          <div key={kpi.label} className="glass-card p-5 rounded-2xl">
            <p className={`text-3xl font-bold ${kpi.color}`}>{loading ? '…' : kpi.value}</p>
            <p className="text-text-secondary text-sm mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Sessions récentes */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="text-text-primary font-semibold">Sessions récentes</h2>
          <Link to="/sessions" className="text-accent text-sm hover:text-accent/80 transition-colors">
            Voir tout →
          </Link>
        </div>
        {loading ? (
          <div className="p-8 text-center text-text-muted text-sm">Chargement…</div>
        ) : recent.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-text-muted text-sm mb-4">Aucune session pour l'instant.</p>
            <Link to="/sessions" className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl inline-block">
              Créer une session →
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {recent.map((s) => {
              const sc = STATUS_CONFIG[s.status] ?? STATUS_CONFIG.PLANNED
              return (
                <Link
                  key={s.id}
                  to={`/sessions/${s.id}`}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-white/[0.03] transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-text-primary text-sm font-medium truncate group-hover:text-accent transition-colors">{s.title}</p>
                    <p className="text-text-muted text-xs mt-0.5">{formatDate(s.startDate)} · {s._count.trainees} stagiaire{s._count.trainees !== 1 ? 's' : ''}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${sc.color} flex-shrink-0`}>
                    {sc.label}
                  </span>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      {/* Liens rapides */}
      <div className="grid grid-cols-2 gap-4">
        <Link to="/sessions" className="glass-card p-5 rounded-2xl hover:bg-white/[0.05] transition-colors group">
          <div className="w-10 h-10 rounded-xl bg-accent/15 border border-accent/20 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
            </svg>
          </div>
          <p className="text-text-primary text-sm font-semibold group-hover:text-accent transition-colors">Sessions de formation</p>
          <p className="text-text-muted text-xs mt-1">Gérer vos sessions et stagiaires</p>
        </Link>
        <Link to="/qualiopi" className="glass-card p-5 rounded-2xl hover:bg-white/[0.05] transition-colors group">
          <div className="w-10 h-10 rounded-xl bg-success/15 border border-success/20 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-success">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <p className="text-text-primary text-sm font-semibold group-hover:text-success transition-colors">Référentiel Qualiopi</p>
          <p className="text-text-muted text-xs mt-1">Suivi des 32 critères qualité</p>
        </Link>
      </div>
    </div>
  )
}
